import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, Clock, RefreshCw, ImageIcon, User, Phone, Scissors, Calendar, DollarSign } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

type Proof = {
  id: number;
  salonId: number;
  sessionId: string;
  clientName: string;
  clientPhone?: string | null;
  serviceName?: string | null;
  professionalName?: string | null;
  appointmentDate?: string | null;
  amount?: string | null;
  imageUrl: string;
  status: "pending" | "approved" | "rejected";
  adminNotes?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
};

export default function AdminPaymentProofs() {
  const utils = trpc.useUtils();
  const [pendingOnly, setPendingOnly] = useState(true);
  const [selectedProof, setSelectedProof] = useState<Proof | null>(null);
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(null);
  const [notes, setNotes] = useState("");

  const { data: proofs, isLoading, refetch } = (trpc as any).paymentProofs.list.useQuery({ pendingOnly });

  const approveMutation = (trpc as any).paymentProofs.approve.useMutation({
    onSuccess: () => {
      toast.success("Comprovante aprovado! Agendamento confirmado.");
      setSelectedProof(null);
      setActionType(null);
      setNotes("");
      refetch();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const rejectMutation = (trpc as any).paymentProofs.reject.useMutation({
    onSuccess: () => {
      toast.success("Comprovante rejeitado.");
      setSelectedProof(null);
      setActionType(null);
      setNotes("");
      refetch();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const handleAction = () => {
    if (!selectedProof || !actionType) return;
    if (actionType === "approve") {
      approveMutation.mutate({ id: selectedProof.id, notes: notes || undefined });
    } else {
      rejectMutation.mutate({ id: selectedProof.id, notes: notes || undefined });
    }
  };

  const statusBadge = (status: string) => {
    if (status === "pending") return <Badge variant="outline" className="text-amber-500 border-amber-500/30 bg-amber-500/10"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
    if (status === "approved") return <Badge variant="outline" className="text-green-500 border-green-500/30 bg-green-500/10"><CheckCircle className="w-3 h-3 mr-1" />Aprovado</Badge>;
    return <Badge variant="outline" className="text-red-500 border-red-500/30 bg-red-500/10"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>;
  };

  const pendingCount = proofs?.filter((p: Proof) => p.status === "pending").length ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Comprovantes de Pagamento</h1>
          <p className="text-sm text-muted-foreground">Aprove ou rejeite comprovantes enviados pelos clientes via agente de IA</p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <Badge className="bg-amber-500 text-white">{pendingCount} pendente{pendingCount > 1 ? "s" : ""}</Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtro */}
      <div className="flex gap-2">
        <Button
          variant={pendingOnly ? "default" : "outline"}
          size="sm"
          onClick={() => setPendingOnly(true)}
        >
          <Clock className="w-4 h-4 mr-2" />
          Pendentes
        </Button>
        <Button
          variant={!pendingOnly ? "default" : "outline"}
          size="sm"
          onClick={() => setPendingOnly(false)}
        >
          Todos
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : !proofs || proofs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle className="w-10 h-10 text-green-500 mb-3" />
            <p className="text-sm font-medium">
              {pendingOnly ? "Nenhum comprovante pendente" : "Nenhum comprovante encontrado"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {pendingOnly
                ? "Todos os comprovantes foram revisados. Ótimo trabalho!"
                : "Os comprovantes enviados pelos clientes aparecerão aqui."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {proofs.map((proof: Proof) => (
            <Card key={proof.id} className={`border-border/50 ${proof.status === "pending" ? "border-amber-500/30 bg-amber-500/5" : ""}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-4">
                  {/* Imagem do comprovante */}
                  <div
                    className="w-20 h-20 rounded-md overflow-hidden border border-border/50 bg-muted cursor-pointer shrink-0 flex items-center justify-center"
                    onClick={() => window.open(proof.imageUrl, "_blank")}
                  >
                    {proof.imageUrl ? (
                      <img src={proof.imageUrl} alt="Comprovante" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>

                  {/* Informações */}
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {statusBadge(proof.status)}
                      <span className="text-xs text-muted-foreground">
                        {new Date(proof.createdAt).toLocaleString("pt-BR")}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <User className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-medium text-foreground">{proof.clientName}</span>
                      </div>
                      {proof.clientPhone && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Phone className="w-3.5 h-3.5 shrink-0" />
                          <span>{proof.clientPhone}</span>
                        </div>
                      )}
                      {proof.serviceName && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Scissors className="w-3.5 h-3.5 shrink-0" />
                          <span>{proof.serviceName}</span>
                        </div>
                      )}
                      {proof.appointmentDate && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Calendar className="w-3.5 h-3.5 shrink-0" />
                          <span>{proof.appointmentDate}</span>
                        </div>
                      )}
                      {proof.amount && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <DollarSign className="w-3.5 h-3.5 shrink-0" />
                          <span className="font-medium text-green-600">R$ {Number(proof.amount).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    {proof.adminNotes && (
                      <p className="text-xs text-muted-foreground italic">Obs: {proof.adminNotes}</p>
                    )}
                  </div>

                  {/* Ações */}
                  {proof.status === "pending" && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                        onClick={() => { setSelectedProof(proof); setActionType("approve"); setNotes(""); }}
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />
                        Aprovar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-500 hover:bg-red-500/10 h-8 text-xs"
                        onClick={() => { setSelectedProof(proof); setActionType("reject"); setNotes(""); }}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" />
                        Rejeitar
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de confirmação */}
      <Dialog open={!!selectedProof && !!actionType} onOpenChange={() => { setSelectedProof(null); setActionType(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve" ? "✅ Aprovar Comprovante" : "❌ Rejeitar Comprovante"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {selectedProof && (
              <div className="bg-muted/50 rounded-md p-3 text-sm space-y-1">
                <p><strong>Cliente:</strong> {selectedProof.clientName}</p>
                {selectedProof.serviceName && <p><strong>Serviço:</strong> {selectedProof.serviceName}</p>}
                {selectedProof.appointmentDate && <p><strong>Data/Hora:</strong> {selectedProof.appointmentDate}</p>}
                {selectedProof.amount && <p><strong>Valor:</strong> R$ {Number(selectedProof.amount).toFixed(2)}</p>}
              </div>
            )}
            {actionType === "approve" && (
              <p className="text-sm text-muted-foreground">
                Ao aprovar, o agendamento será confirmado automaticamente e o cliente receberá uma notificação via WhatsApp (se configurado).
              </p>
            )}
            {actionType === "reject" && (
              <p className="text-sm text-muted-foreground">
                Ao rejeitar, o agendamento será cancelado e o cliente receberá uma notificação via WhatsApp (se configurado).
              </p>
            )}
            <div className="space-y-2">
              <Label className="text-xs">Observação (opcional)</Label>
              <Textarea
                placeholder={actionType === "reject" ? "Ex: Comprovante ilegível, valor incorreto..." : "Ex: Pagamento confirmado, obrigado!"}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedProof(null); setActionType(null); }}>
              Cancelar
            </Button>
            <Button
              onClick={handleAction}
              disabled={approveMutation.isPending || rejectMutation.isPending}
              className={actionType === "approve" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
            >
              {(approveMutation.isPending || rejectMutation.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {actionType === "approve" ? "Confirmar Aprovação" : "Confirmar Rejeição"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
